import { buildMarketDataPreferenceRequest } from "./marketDataPreferenceSave";
import {
  defaultMarketDataSourceOption,
  findMarketDataSourceIndex,
  formatMarketDataSourceUnavailableReason,
  hasSavedMarketDataSourcePreference,
  indexOfMatchingMarketDataSource,
  isIbkrMarketDataBroker,
  isMarketDataSourceOptionAvailable,
  marketDataBrokerKey,
  marketDataSourcesForInstrumentEditor,
  needsMarketDataSourceChoice,
  resolveMarketDataSourceOptions,
} from "./marketDataSourceChoice";
import type { ResolveInstrumentRequest, ResolveInstrumentResponse } from "../types/instruments";
import type {
  InstrumentSetupRow,
  MarketDataPreferenceBatchItem,
  QuoteVariantCandidate,
} from "../types/instrumentSetup";
import type { MarketDataSourceOption } from "../types/marketDataSources";
import { requiresMarketDataConsentForSetup } from "./marketDataAttributionCopy";

export type InstrumentRowEditorSelection = {
  assetClass: string;
  resolvedSymbol?: string;
  marketDataSource?: MarketDataSourceOption;
  marketDataBroker?: string;
};

export function normalizeQuoteVariantCandidates(
  candidates: InstrumentSetupRow["quote_variant_candidates"] | string[] | undefined
): QuoteVariantCandidate[] {
  if (!Array.isArray(candidates)) return [];
  return candidates
    .map((item) => {
      if (typeof item === "string") {
        return { symbol: item };
      }
      if (item && typeof item === "object" && typeof item.symbol === "string") {
        return item;
      }
      return null;
    })
    .filter((item): item is QuoteVariantCandidate => Boolean(item?.symbol?.trim()));
}

/** Value sent to resolve/save — prefer BE display_symbol over short registry symbol. */
export function quoteVariantOptionValue(candidate: QuoteVariantCandidate): string {
  return candidate.display_symbol?.trim() || candidate.symbol.trim();
}

/**
 * Unique select `value` — must be `symbol`, not display_symbol.
 * Multiple candidates can share the same display_symbol (e.g. AAVEUSDT + AAVEUSDTPERP
 * both → AAVEUSDTPERP); Radix Select concatenates duplicate ItemTexts in the trigger.
 */
export function quoteVariantSelectValue(candidate: QuoteVariantCandidate): string {
  return candidate.symbol.trim();
}

/**
 * Perp / …PERP symbols map to futures OHLC; plain USDT/USDC/USD quote pairs map to spot.
 * Infer from the picker `symbol` only (not display_symbol — that can remap spot rows to PERP names).
 */
export function inferQuoteVariantProductLane(
  symbolOrCandidate: string | Pick<QuoteVariantCandidate, "symbol">
): "spot" | "futures" | undefined {
  const upper = (
    typeof symbolOrCandidate === "string"
      ? symbolOrCandidate
      : symbolOrCandidate.symbol || ""
  )
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "")
  if (!upper) return undefined
  if (/PERP$/i.test(upper) || upper.includes("PERP")) {
    return "futures"
  }
  // Crypto quote suffixes without PERP → spot lane for Binance-style OHLC.
  if (/(USDT|USDC|BUSD|USD)$/i.test(upper)) {
    return "spot"
  }
  return undefined
}

/** BE gate for picker enablement — any OHLC provider lane (CoinAPI, Twelve Data, broker), not Binance-only. */
export function isQuoteVariantOhlcSupported(candidate: QuoteVariantCandidate): boolean {
  return candidate.md_supported !== false;
}

/** Product picker labels — spot/perp lane only; never show OHLC vendor on variant rows. */
export function stripQuoteVariantVendorFromPickerLabel(label: string): string {
  const parts = label
    .split("·")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length >= 3) {
    return parts.slice(0, -1).join(" · ");
  }
  return label.trim();
}

export function quoteVariantOptionLabel(candidate: QuoteVariantCandidate): string {
  const pickerLabel = candidate.picker_label?.trim();
  if (pickerLabel) {
    let label = stripQuoteVariantVendorFromPickerLabel(pickerLabel);
    if (!isQuoteVariantOhlcSupported(candidate)) {
      label = `${label} · OHLC unavailable`;
    }
    return label;
  }
  const symbol = quoteVariantSelectValue(candidate) || quoteVariantOptionValue(candidate);
  const lane = inferQuoteVariantProductLane(candidate);
  const laneTag = lane === "futures" ? "Futures" : lane === "spot" ? "Spot" : null;
  let label = laneTag ? `${symbol} · ${laneTag}` : symbol;
  if (!isQuoteVariantOhlcSupported(candidate)) {
    label = `${label} · OHLC unavailable`;
  }
  return label;
}

export function findQuoteVariantCandidate(
  candidates: QuoteVariantCandidate[],
  hint: string | null | undefined
): QuoteVariantCandidate | undefined {
  const needle = hint?.trim();
  if (!needle) return undefined;
  return candidates.find(
    (c) =>
      quoteVariantSelectValue(c) === needle ||
      quoteVariantOptionValue(c) === needle ||
      c.symbol.trim() === needle
  );
}

/** Map a picker selection (symbol or qualified) to the resolve/save symbol. */
export function qualifiedQuoteVariantForResolve(
  candidates: QuoteVariantCandidate[],
  selected: string | null | undefined
): string {
  const match = findQuoteVariantCandidate(candidates, selected);
  if (match) return quoteVariantOptionValue(match);
  return selected?.trim() || "";
}

/** True when resolve must stop for mandatory quote-variant picker (non-HL auto-map). */
export function isQuoteVariantPickerRequired(input: {
  requires_quote_variant_choice?: boolean;
  resolved?: boolean;
  auto_resolved_quote_variant?: string | null;
}): boolean {
  if (!input.requires_quote_variant_choice) return false;
  if (input.resolved === true && input.auto_resolved_quote_variant?.trim()) {
    return false;
  }
  return true;
}

export function isQuoteVariantOverridable(input: {
  quote_variant_overridable?: boolean;
  quote_variant_candidates?: QuoteVariantCandidate[] | null;
}): boolean {
  return Boolean(
    input.quote_variant_overridable &&
      normalizeQuoteVariantCandidates(input.quote_variant_candidates ?? undefined).length > 0
  );
}

export function showQuoteVariantPickerForRow(
  row: Pick<
    InstrumentSetupRow,
    | "requires_quote_variant_choice"
    | "quote_variant_overridable"
    | "quote_variant_candidates"
    | "resolved"
    | "auto_resolved_quote_variant"
  >
): boolean {
  const candidates = normalizeQuoteVariantCandidates(row.quote_variant_candidates);
  if (candidates.length === 0) return false;
  return isQuoteVariantPickerRequired(row) || isQuoteVariantOverridable(row);
}

export function qualifiedActiveQuoteVariant(
  resolvedSymbol: string | undefined,
  quoteCandidates: QuoteVariantCandidate[]
): string {
  const trimmed = resolvedSymbol?.trim();
  if (!trimmed) return "";
  return qualifiedQuoteVariantForResolve(quoteCandidates, trimmed) || trimmed;
}

export function savedQuoteVariantForRow(
  row: Pick<InstrumentSetupRow, "suggested_quote_variant" | "market_data_symbol">
): string {
  return row.suggested_quote_variant?.trim() || row.market_data_symbol?.trim() || "";
}

/** True when the picker matches a persisted quote-variant preference on this trade label. */
export function activeQuoteVariantMatchesSavedPref(input: {
  row: Pick<InstrumentSetupRow, "suggested_quote_variant" | "market_data_symbol">;
  activeResolvedSymbol?: string;
  quoteCandidates?: QuoteVariantCandidate[];
}): boolean {
  const saved = savedQuoteVariantForRow(input.row);
  if (!saved) return false;
  const active = qualifiedActiveQuoteVariant(
    input.activeResolvedSymbol,
    normalizeQuoteVariantCandidates(input.quoteCandidates)
  );
  if (!active) return false;
  return active.toUpperCase() === saved.toUpperCase();
}

/**
 * Pick default OHLC listing after a quote-variant re-resolve.
 * Trust BE `market_data_sources[]` order and is_selected / is_default flags.
 * Ignore quote-variant candidate market_data_broker — that field is product metadata only.
 */
export function preferredSourceForActiveQuoteVariant(input: {
  row: InstrumentSetupRow;
  resolveResult?: ResolveInstrumentResponse | null;
  activeResolvedSymbol?: string;
  quoteCandidates?: QuoteVariantCandidate[];
}): MarketDataSourceOption | null {
  const sources = marketDataSourcesForQuoteVariantEditor(
    input.resolveResult,
    input.row,
    input.activeResolvedSymbol
  );
  if (!sources.length) return null;

  const resolveResult = input.resolveResult;
  if (resolveResult?.selected_market_data_source) {
    const idx = indexOfMatchingMarketDataSource(
      sources,
      resolveResult.selected_market_data_source
    );
    if (idx >= 0) return sources[idx];
  }

  if (
    activeQuoteVariantMatchesSavedPref({
      row: input.row,
      activeResolvedSymbol: input.activeResolvedSymbol,
      quoteCandidates: input.quoteCandidates,
    }) ||
    hasSavedMarketDataSourcePreference(input.row)
  ) {
    const saved = input.row.selected_market_data_source;
    if (saved) {
      const idx = indexOfMatchingMarketDataSource(sources, saved);
      if (idx >= 0) return sources[idx];
    }
    const listingKey = input.row.md_listing_key?.trim();
    if (listingKey) {
      const idx = sources.findIndex(
        (source) => source.md_listing_key?.trim() === listingKey
      );
      if (idx >= 0) return sources[idx];
    }
    const selectedIdx = sources.findIndex((source) => source.is_selected);
    if (selectedIdx >= 0) return sources[selectedIdx];
  }

  return defaultMarketDataSourceOption(sources) ?? sources[0] ?? null;
}

export function sourceIndexForActiveQuoteVariant(input: {
  row: InstrumentSetupRow;
  resolveResult?: ResolveInstrumentResponse | null;
  activeResolvedSymbol?: string;
  quoteCandidates?: QuoteVariantCandidate[];
}): number {
  const sources = marketDataSourcesForQuoteVariantEditor(
    input.resolveResult,
    input.row,
    input.activeResolvedSymbol
  );
  const preferred = preferredSourceForActiveQuoteVariant({
    ...input,
    resolveResult: input.resolveResult,
  });
  return findMarketDataSourceIndex(sources, preferred);
}

/** True when the selected product variant still needs a fresh /resolve for its OHLC menu. */
export function needsOhlcLaneRefresh(input: {
  resolveResult?: ResolveInstrumentResponse | null;
  row: InstrumentSetupRow;
  activeResolvedSymbol?: string;
  quoteCandidates?: QuoteVariantCandidate[];
}): boolean {
  const quote = input.activeResolvedSymbol?.trim();
  if (!quote) return false;

  const activeQualified = qualifiedActiveQuoteVariant(
    quote,
    normalizeQuoteVariantCandidates(input.quoteCandidates)
  );
  if (!activeQualified) return false;

  const resolveResult = input.resolveResult;
  if (resolveResult) {
    const resolvedProduct =
      resolveResult.market_data_symbol?.trim() ||
      resolveResult.suggested_quote_variant?.trim() ||
      "";
    if (
      resolvedProduct &&
      resolvedProduct.toUpperCase() === activeQualified.toUpperCase()
    ) {
      return false;
    }
    if (resolvedProduct) return true;
  }

  return !activeQuoteVariantMatchesSavedPref({
    row: input.row,
    activeResolvedSymbol: quote,
    quoteCandidates: input.quoteCandidates,
  });
}

/** User-facing copy when resolve returned no usable OHLC lanes for the picked variant. */
export function describeEmptyOhlcSourcesForQuoteVariant(input: {
  activeResolvedSymbol?: string;
  quoteCandidates?: QuoteVariantCandidate[];
}): string {
  const quote = input.activeResolvedSymbol?.trim();
  if (quote) {
    return `No OHLC sources available for ${quote} yet. Use Refresh sources or pick another quote variant.`;
  }
  return "No market data sources returned for this symbol yet. Use Refresh sources.";
}

/** Seed hint for quote-variant picker — suggested_quote_variant tracks active market_data_symbol. */
export function seedQuoteVariantHintForRow(
  row: Pick<
    InstrumentSetupRow,
    | "auto_resolved_quote_variant"
    | "market_data_symbol"
    | "suggested_quote_variant"
  >
): string | null | undefined {
  return (
    row.suggested_quote_variant?.trim() ||
    row.market_data_symbol?.trim() ||
    row.auto_resolved_quote_variant?.trim() ||
    undefined
  );
}

/** @deprecated BE applies unified OHLC policy — FE uses market_data_sources[] as returned. */
export function filterMarketDataSourcesForQuoteVariant(
  sources: MarketDataSourceOption[],
  _activeResolvedSymbol?: string,
  _quoteCandidates?: QuoteVariantCandidate[] | undefined
): MarketDataSourceOption[] {
  return sources;
}

export function marketDataSourcesForQuoteVariantEditor(
  resolveResult: ResolveInstrumentResponse | null | undefined,
  row: InstrumentSetupRow,
  _activeResolvedSymbol?: string,
  _options?: { allowStaleLaneFallback?: boolean }
): MarketDataSourceOption[] {
  return marketDataSourcesForInstrumentEditor(resolveResult, row);
}

/** Resolve OHLC menu from a resolve payload — pass through BE ordering/policy. */
export function resolveMarketDataSourcesForQuoteVariant(input: {
  market_data_sources?: MarketDataSourceOption[];
  candidate_brokers?: ResolveInstrumentResponse["candidate_brokers"];
  activeResolvedSymbol?: string;
  quoteCandidates?: QuoteVariantCandidate[];
}): MarketDataSourceOption[] {
  return resolveMarketDataSourceOptions(input);
}

/** Prefer suggested / auto-resolved / first candidate that can actually fetch OHLC. Returns select value. */
export function resolveInitialQuoteVariant(
  suggested: string | null | undefined,
  candidates: QuoteVariantCandidate[],
  autoResolved?: string | null
): string {
  const ohlcReady = isQuoteVariantOhlcSupported;

  const suggestedMatch = findQuoteVariantCandidate(candidates, suggested);
  if (suggestedMatch && ohlcReady(suggestedMatch)) {
    return quoteVariantSelectValue(suggestedMatch);
  }

  const autoMatch = findQuoteVariantCandidate(candidates, autoResolved);
  if (autoMatch && ohlcReady(autoMatch)) {
    return quoteVariantSelectValue(autoMatch);
  }

  const firstReady = candidates.find(ohlcReady);
  if (firstReady) return quoteVariantSelectValue(firstReady);

  if (suggestedMatch) return quoteVariantSelectValue(suggestedMatch);
  if (autoMatch) return quoteVariantSelectValue(autoMatch);
  const first = candidates[0];
  if (first) return quoteVariantSelectValue(first);
  return suggested?.trim() || autoResolved?.trim() || "";
}

/**
 * Symbol for POST /instruments/resolve.
 * Asset-class / HIP-3 proxy: always the trade symbol (e.g. SILVER(XYZ)).
 * Quote-variant lane: the selected qualified symbol (e.g. AAVEUSDCPERP).
 *
 * @deprecated Prefer {@link buildInstrumentResolveRequest} — resolve `symbol` stays the trade label;
 * qualified variants go on `resolved_symbol`.
 */
export function symbolForInstrumentResolve(input: {
  tradeSymbol: string;
  resolvedSymbol?: string | null;
  requiresQuoteVariantChoice?: boolean;
  /** When provided, maps picker `symbol` values up to display_symbol for resolve. */
  quoteCandidates?: QuoteVariantCandidate[];
}): string {
  if (input.requiresQuoteVariantChoice) {
    if (input.quoteCandidates?.length) {
      return qualifiedQuoteVariantForResolve(input.quoteCandidates, input.resolvedSymbol);
    }
    return input.resolvedSymbol?.trim() || "";
  }
  return input.tradeSymbol.trim();
}

/** Maps picker selection to BE `resolved_symbol` (display_symbol when present). */
export function resolvedSymbolForInstrumentResolve(
  resolvedSymbol: string | null | undefined,
  quoteCandidates?: QuoteVariantCandidate[]
): string | undefined {
  const trimmed = resolvedSymbol?.trim();
  if (!trimmed) return undefined;
  if (quoteCandidates?.length) {
    return qualifiedQuoteVariantForResolve(quoteCandidates, trimmed) || trimmed;
  }
  return trimmed;
}

/** POST /instruments/resolve — trade label on `symbol`, variant lane on `resolved_symbol`. */
export function buildInstrumentResolveRequest(input: {
  tradeSymbol: string;
  assetClass?: string;
  brokerAccountCode?: string;
  resolvedSymbol?: string | null;
  quoteCandidates?: QuoteVariantCandidate[];
}): ResolveInstrumentRequest {
  const symbol = input.tradeSymbol.trim();
  const body: ResolveInstrumentRequest = {
    symbol,
    asset_class: input.assetClass?.trim() || undefined,
    broker_account_code: input.brokerAccountCode?.trim() || undefined,
  };
  const trimmedSelection = input.resolvedSymbol?.trim();
  const match =
    trimmedSelection && input.quoteCandidates?.length
      ? findQuoteVariantCandidate(input.quoteCandidates, trimmedSelection)
      : undefined;
  const resolved_symbol = resolvedSymbolForInstrumentResolve(
    input.resolvedSymbol,
    input.quoteCandidates
  );
  if (resolved_symbol) {
    body.resolved_symbol = resolved_symbol;
    body.market_data_symbol = resolved_symbol;
  }
  const instrumentId = match?.instrument_id?.trim();
  if (instrumentId) {
    body.instrument_id = instrumentId;
  }
  return body;
}

/** Prefer BE proxy / quote suggestion for save when the picker has none yet. */
export function resolvedSymbolForInstrumentSave(input: {
  selectionResolvedSymbol?: string | null;
  suggestedQuoteVariant?: string | null;
  resolveSuggestedQuoteVariant?: string | null;
  autoResolvedQuoteVariant?: string | null;
  quoteCandidates?: QuoteVariantCandidate[];
}): string | undefined {
  const fromSelection = input.selectionResolvedSymbol?.trim();
  if (fromSelection) {
    if (input.quoteCandidates?.length) {
      return qualifiedQuoteVariantForResolve(input.quoteCandidates, fromSelection) || fromSelection;
    }
    return fromSelection;
  }
  const fromSaved = input.suggestedQuoteVariant?.trim();
  if (fromSaved) {
    if (input.quoteCandidates?.length) {
      return qualifiedQuoteVariantForResolve(input.quoteCandidates, fromSaved) || fromSaved;
    }
    return fromSaved;
  }
  const fromResolve = input.resolveSuggestedQuoteVariant?.trim();
  if (fromResolve) return fromResolve;
  const fromAuto = input.autoResolvedQuoteVariant?.trim();
  if (fromAuto) return fromAuto;
  return undefined;
}

export type QuoteVariantSelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

export function quoteVariantSelectOptions(candidates: QuoteVariantCandidate[]): QuoteVariantSelectOption[] {
  const seen = new Set<string>();
  const options: QuoteVariantSelectOption[] = [];
  for (const candidate of candidates) {
    const value = quoteVariantSelectValue(candidate);
    if (!value || seen.has(value)) continue;
    seen.add(value);
    options.push({
      value,
      label: quoteVariantOptionLabel(candidate),
      disabled: !isQuoteVariantOhlcSupported(candidate),
    });
  }
  return options;
}

export function buildInstrumentSetupBatchItem(
  brokerAccountCode: string,
  tradeSymbol: string,
  selection: InstrumentRowEditorSelection,
  resolveContext?: Pick<
    ResolveInstrumentResponse,
    "md_listing_key" | "instrument_id" | "asset_class"
  > | null,
  row?: Pick<
    InstrumentSetupRow,
    | "md_listing_key"
    | "instrument_id"
    | "quote_variant_candidates"
    | "requires_quote_variant_choice"
    | "quote_variant_overridable"
    | "requires_market_data_consent"
    | "saved_market_data_broker"
    | "suggested_quote_variant"
    | "market_data_symbol"
  > | null,
  consentGranted = true
): MarketDataPreferenceBatchItem {
  const quoteCandidates = normalizeQuoteVariantCandidates(row?.quote_variant_candidates);
  const shouldQualifyResolvedSymbol =
    Boolean(row?.requires_quote_variant_choice || row?.quote_variant_overridable) &&
    Boolean(selection.resolvedSymbol?.trim());
  const variantLaneChanged =
    shouldQualifyResolvedSymbol &&
    !activeQuoteVariantMatchesSavedPref({
      row: row ?? {},
      activeResolvedSymbol: selection.resolvedSymbol,
      quoteCandidates,
    });
  const mergedResolveContext = {
    md_listing_key:
      selection.marketDataSource?.md_listing_key ??
      (variantLaneChanged ? undefined : resolveContext?.md_listing_key ?? row?.md_listing_key),
    instrument_id: resolveContext?.instrument_id ?? row?.instrument_id,
    asset_class: resolveContext?.asset_class,
  };
  // Pref key stays the CSV/trade symbol; OHLC lane goes on resolved_symbol only.
  const resolvedForPref = shouldQualifyResolvedSymbol
    ? qualifiedQuoteVariantForResolve(quoteCandidates, selection.resolvedSymbol) ||
      selection.resolvedSymbol!.trim()
    : selection.resolvedSymbol?.trim();
  const request = buildMarketDataPreferenceRequest(
    brokerAccountCode,
    tradeSymbol,
    {
      assetClass: selection.assetClass,
      marketDataBroker: selection.marketDataBroker,
      marketDataSource: selection.marketDataSource,
    },
    mergedResolveContext,
    { resolvedSymbol: resolvedForPref }
  );
  return {
    ...request,
    broker_account_code: brokerAccountCode,
    symbol: tradeSymbol,
    consent: consentGranted,
  } as MarketDataPreferenceBatchItem;
}

export function describeInstrumentSetupSaveBlockers(input: {
  row: InstrumentSetupRow;
  selection: InstrumentRowEditorSelection;
  consentGranted: boolean;
}): string | null {
  const { row, selection, consentGranted } = input;
  if (!selection.assetClass.trim()) return "Select an asset class.";
  if (isQuoteVariantPickerRequired(row) && !selection.resolvedSymbol?.trim()) {
    return "Choose a quote or perp variant.";
  }
  if (
    requiresMarketDataConsentForSetup({
      row,
      source: selection.marketDataSource,
    }) &&
    !consentGranted
  ) {
    return "Check the consent box before confirming this OHLC source.";
  }
  if (
    (row.requires_market_data_source_choice || row.requires_market_data_broker_choice) &&
    !selection.marketDataSource
  ) {
    return "Select an OHLC source.";
  }
  const source = selection.marketDataSource;
  const brokerKey = source
    ? marketDataBrokerKey(source)
    : (selection.marketDataBroker || "").trim().toLowerCase();
  if (
    source &&
    isIbkrMarketDataBroker(brokerKey) &&
    (source.ibkr_conid == null || Number.isNaN(Number(source.ibkr_conid)))
  ) {
    return "Select a specific IBKR contract month (not only the exchange row).";
  }
  if (source && !isMarketDataSourceOptionAvailable(source)) {
    const reason = (source.unavailable_reason ?? "").trim().toLowerCase();
    if (reason === "broker_not_connected") {
      return "Connect this broker before confirming, or pick another OHLC source.";
    }
    return (
      formatMarketDataSourceUnavailableReason(source.unavailable_reason) ??
      "Selected OHLC source is not available. Connect the broker or pick another listing."
    );
  }
  return null;
}

/** True when the user must confirm/save defaults even if they did not edit the form. */
export function needsInstrumentSetupInitialConfirm(
  row: InstrumentSetupRow,
  source?: MarketDataSourceOption | null
): boolean {
  if (row.setup_skipped) return false;

  if (row.setup_complete === true) return false;
  if (row.setup_complete === false) return true;

  if (row.status === "pending") return true;

  // Resolved on scan ≠ preference saved — keep Confirm enabled until BE has a stored pref.
  if (!hasSavedMarketDataSourcePreference(row)) {
    return true;
  }

  if (row.requires_asset_class_confirmation) return true;
  if (isQuoteVariantPickerRequired(row)) return true;
  if (
    requiresMarketDataConsentForSetup({
      row,
      source: source ?? row.selected_market_data_source ?? null,
    })
  ) {
    return true;
  }
  if (
    needsMarketDataSourceChoice({
      requires_market_data_source_choice: row.requires_market_data_source_choice,
      selected_market_data_source: row.selected_market_data_source,
      md_listing_key: row.md_listing_key,
      resolved: row.resolved,
      market_data_sources: row.market_data_sources,
      candidate_brokers: row.candidate_brokers,
      saved_market_data_broker: row.saved_market_data_broker,
      saved_ibkr_conid: row.saved_ibkr_conid,
      saved_ibkr_exchange: row.saved_ibkr_exchange,
    })
  ) {
    return true;
  }
  return false;
}

export function isInstrumentSetupRowReadyToSave(input: {
  row: InstrumentSetupRow;
  selection: InstrumentRowEditorSelection;
  consentGranted: boolean;
}): boolean {
  return describeInstrumentSetupSaveBlockers(input) === null;
}

export function resolveSymbolForInstrumentSetupRow(
  row: InstrumentSetupRow,
  selection: InstrumentRowEditorSelection
): string {
  return (
    symbolForInstrumentResolve({
      tradeSymbol: row.symbol,
      resolvedSymbol: selection.resolvedSymbol,
      requiresQuoteVariantChoice: row.requires_quote_variant_choice,
    }) || row.symbol.trim()
  );
}
